import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Compass, ArrowLeft, Sun, Moon } from 'lucide-react';

const AuthLayout = ({ children, title, subtitle, isDarkMode, setIsDarkMode }) => {
    return (
        <div className={`min-h-screen flex flex-col items-center justify-center p-6 font-sans overflow-hidden relative transition-colors duration-500 ${isDarkMode ? 'bg-cosmic-900 text-white' : 'bg-[#F8FAFC] text-slate-900'
            }`}>
            {/* Background Glows */}
            {isDarkMode ? (
                <>
                    <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none animate-breathe" />
                    <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-mystic-purple/10 rounded-full blur-[120px] pointer-events-none animate-breathe" style={{ animationDelay: '2s' }} />
                </>
            ) : (
                <>
                    <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-[100px] pointer-events-none" />
                    <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-gold-400/10 rounded-full blur-[100px] pointer-events-none" />
                </>
            )}

            {/* Back Navigation & Theme Toggle */}
            <div className="absolute top-8 left-8 right-8 flex justify-between items-center z-20">
                <Link
                    to="/"
                    className={`group flex items-center space-x-3 transition-all duration-300 ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-slate-400 hover:text-slate-900'
                        }`}
                >
                    <div className={`w-11 h-11 border rounded-[14px] flex items-center justify-center transition-all shadow-sm ${isDarkMode
                        ? 'bg-white/[0.03] border-white/[0.08] group-hover:bg-white/[0.08] group-hover:border-white/[0.15]'
                        : 'bg-white border-slate-200 group-hover:bg-slate-50 group-hover:border-slate-300'
                        }`}>
                        <ArrowLeft className="w-5 h-5" />
                    </div>
                    <span className="text-sm font-black tracking-tight uppercase">Home</span>
                </Link>

                <button
                    onClick={() => setIsDarkMode(!isDarkMode)}
                    className={`p-2.5 rounded-xl transition-all shadow-sm ${isDarkMode ? 'bg-white/5 hover:bg-white/10 text-gold-400' : 'bg-white hover:bg-slate-50 text-slate-600 border border-slate-200'
                        }`}
                >
                    {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="w-full max-w-[420px] z-10"
            >
                {/* Logo Section */}
                <div className="flex justify-center mb-10">
                    <Link to="/" className="flex flex-col items-center space-y-4 group">
                        <img
                            src="/icon-192x192.png"
                            alt="Swara Shastra Logo"
                            className={`w-20 h-20 object-contain rounded-[24px] shadow-2xl transition-transform group-hover:scale-105 ${isDarkMode ? 'shadow-blue-500/20' : 'shadow-slate-200/50'
                                }`}
                        />
                        <div className="flex flex-col items-center">
                            <span className={`text-2xl font-black tracking-tighter leading-none ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                SWARA SHASTRA
                            </span>
                            <div className="flex items-center space-x-2 mt-1">
                                <div className="h-[2px] w-8 bg-blue-500 rounded-full" />
                                <div className="h-[2px] w-2 bg-slate-200 rounded-full" />
                                <div className="h-[2px] w-8 bg-gold-500 rounded-full" />
                            </div>
                        </div>
                    </Link>
                </div>

                <div className={`rounded-[32px] p-8 sm:p-10 transition-colors duration-500 relative ${isDarkMode
                    ? 'glass-card border-white/[0.08]'
                    : 'bg-white border border-slate-200 shadow-xl text-slate-900'
                    }`}>
                    <div className="text-center mb-10">
                        <h1 className={`text-3xl font-black tracking-tight mb-2 uppercase ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {title}
                        </h1>
                        <p className={`text-sm font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                            {subtitle}
                        </p>
                    </div>

                    {children}
                </div>
            </motion.div>
        </div>
    );
};

export default AuthLayout;
